/**
 * Theme colors — the single semantic palette for Helix 2000 UI.
 *
 * Values are extracted 1:1 from the de-facto palette that was hardcoded
 * across ~300 call sites plus the partial token files
 * (config/ui/hudDefaults.config.ts, netobs/colors.ts). Adopting these
 * tokens is therefore a VISUAL NO-OP — a component swaps a literal for a
 * named token with identical output.
 *
 * Consume the SEMANTIC `COLORS` map (textPrimary / danger / surface …),
 * not raw hex. `RAW` is exported only for the rare primitive that needs a
 * specific physical hue; new UI should prefer semantic names.
 *
 * The netobs domain hues stay authoritative in `netobs/colors.ts` (their
 * colour-blindness rationale lives there); they are re-exported here as
 * `COLORS.netobs` so there is ONE import path. theme → netobs only (no cycle).
 */
import { NETOBS_COLORS } from '../netobs/colors';
import { resolveToCharacterClass } from '../../../shared/classes/CallingDisplayRegistry';

/** Physical palette — deduped from the literals found across the UI. */
export const RAW = {
  white: '#ffffff',
  black: '#000000',
  // greens (success)
  green: '#2ecc71',
  greenAlt: '#27ae60',
  mint: '#51cf66',
  netGreen: '#42d77d',
  // reds (danger)
  red: '#e74c3c',
  scarlet: '#ff4757',
  coral: '#ff5f5f',
  salmon: '#ff6b6b',
  // yellows (warning) / gold (accent)
  gold: '#ffd700',
  amber: '#ffd23f',
  yellow: '#f1c40f',
  orangeWarn: '#ffa502',
  // blues (info / mana)
  blue: '#3498db',
  sky: '#4ea3ff',
  ice: '#74c0fc',
  // brand
  orange: '#ff6b00',
  // neutrals
  slate: '#7f8c8d',
  ink: '#2c3e50',
  gray: '#888888',
  silver: '#aaaaaa',
  cloud: '#ecf0f1',
} as const;

export const COLORS = {
  // ── text ─────────────────────────────────────────────────────
  textPrimary: RAW.white,
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  textAccent: RAW.gold,
  textDisabled: 'rgba(255, 255, 255, 0.35)',

  // ── surfaces ─────────────────────────────────────────────────
  surface: 'rgba(20, 20, 30, 0.98)', // ToolWindow / panel base
  surfaceRaised: 'rgba(40, 44, 60, 0.96)',
  surfaceSunken: 'rgba(0, 0, 0, 0.5)',
  surfaceHover: 'rgba(255, 255, 255, 0.08)',
  overlayScrim: 'rgba(0, 0, 0, 0.6)',
  overlayScrimStrong: 'rgba(0, 0, 0, 0.8)',
  overlayScrimLight: 'rgba(0, 0, 0, 0.3)',

  // ── borders ──────────────────────────────────────────────────
  border: 'rgba(255, 255, 255, 0.2)',
  borderSubtle: 'rgba(255, 255, 255, 0.1)',
  borderFaint: 'rgba(255, 255, 255, 0.05)',
  borderStrong: '#3a4456',

  // ── status (semantic) ────────────────────────────────────────
  success: RAW.netGreen,
  warning: RAW.amber,
  danger: RAW.scarlet,
  info: RAW.sky,

  // ── resources (gameplay) ─────────────────────────────────────
  health: RAW.scarlet,
  mana: RAW.blue,
  xp: RAW.gold,

  // ── brand (MapleStory aesthetic) ─────────────────────────────
  brand: RAW.orange,
  brandAccent: RAW.gold,

  // ── domain re-export (single import path) ────────────────────
  netobs: NETOBS_COLORS,
} as const;

export type SemanticColorKey = keyof typeof COLORS;

/**
 * HUD-restyle accent tokens — the "ink & brass" fitting tones (style guide §2/§4).
 * Extracted 1:1 from the approved player-frame mock
 * (docs/design/ui-restyle/mocks/player-frame.html): brass base is #c4a469,
 * used at ~38% opacity as the panel/slot border ("fitting") and at full
 * opacity for the hover/active/edge-accent bright state. These are the ONLY
 * new hues the restyle introduces — everything else stays in COLORS. Not
 * gameplay-semantic (class/rarity colors stay untouched).
 */
export const ACCENT = {
  brass: 'rgba(196, 164, 105, 0.38)', // border / fitting tone (mock --brass)
  brassBright: '#c4a469', // hover / active / edge-accent (mock --brass-bright)
} as const;

/**
 * Numeric (0xRRGGBB) twin of ACCENT.brassBright for the world-space Pixi
 * surfaces the restyle also touches (interaction prompts, nameplate fittings) —
 * the IGraphics/INameTag fill+stroke API takes numbers, not CSS strings, so
 * Pixi consumers read the hue from here instead of re-deriving `parseInt`.
 * Same #c4a469 hue as ACCENT.brassBright; alpha (the 0.38 in ACCENT.brass) is
 * applied by the consumer via its own alpha arg. One SSOT, two forms.
 */
export const ACCENT_NUM = {
  brassBright: 0xc4a469,
} as const;

/**
 * INK ramp — the "ink & brass" panel/well material (style guide §1/§2). The
 * dark gradient body every restyled surface sits on: `hi → base → lo` is the
 * flagship player-frame panel gradient (top-lit, ~18% darker at the bottom per
 * §1's gradient discipline); `well → wellLo` is the recessed hotbar-slot well.
 * Extracted 1:1 from the approved player-frame mock
 * (docs/design/ui-restyle/mocks/player-frame.html — `--ink-hi/--ink/--ink-lo`
 * + the `.slot` background). A shared token, NOT a per-component palette (§7) —
 * the player frame and the hotbar wells both draw from it.
 */
export const INK = {
  hi: '#232a33', // panel top (mock --ink-hi)
  base: '#171c23', // panel mid (mock --ink)
  lo: '#10141a', // panel bottom (mock --ink-lo)
  well: '#1b212a', // slot well top (mock .slot gradient)
  wellLo: '#12161d', // slot well bottom
} as const;

/**
 * MOOD ramp — the panel-base warmth tint for `SCENE_MOOD` reactivity
 * (style guide §6). `useHudMood()` (Lane A.4) subscribes to SCENE_MOOD once
 * and interpolates a clamped tint between `cold` and `warm`; components
 * consume the resulting token, never the bus. Clamped to ~6% alpha so a
 * beach HUD reads faintly warm and an undercroft faintly cold WITHOUT ever
 * touching text or bar colors (legibility never trades — §7). `?hudMood=0`
 * disables it entirely.
 */
export const MOOD = {
  warm: 'rgba(224, 156, 74, 0.06)', // bright/beach biomes → faint warmth
  cold: 'rgba(74, 130, 224, 0.06)', // dark/undercroft biomes → faint cold
  neutral: 'rgba(0, 0, 0, 0)',
  maxTintAlpha: 0.06, // clamp ceiling (~6% per §6 — never higher)
} as const;

/** RGB triples backing MOOD.warm / MOOD.cold (for the continuous ramp). */
const MOOD_WARM_RGB = [224, 156, 74] as const;
const MOOD_COLD_RGB = [74, 130, 224] as const;

/**
 * Pure SCENE_MOOD-intensity → panel-tint mapping (consumed by useHudMood,
 * Lane A.4). `SCENE_MOOD.intensity` is a drama/level MULTIPLIER centered on 1
 * (1 = neutral baseline, >1 = swell, <1 = lull), NOT a temperature axis — so
 * this maps the deviation from baseline to a clamped warm(>1)/cold(<1) tint.
 *
 * ⚠️ tune-later: the style-guide example (beach warm / undercroft cold) wants a
 * true biome-temperature signal; intensity can't distinguish a hot beach from a
 * cold-but-dramatic night. Today: swells warm the HUD, lulls cool it. Swap the
 * driver signal here when a temperature axis exists — consumers keep the token.
 */
export function moodTintFor(intensity: number): string {
  const warmth = Math.max(-1, Math.min(1, (Number.isFinite(intensity) ? intensity : 1) - 1));
  if (warmth === 0) return MOOD.neutral;
  const alpha = Math.abs(warmth) * MOOD.maxTintAlpha;
  const [r, g, b] = warmth > 0 ? MOOD_WARM_RGB : MOOD_COLD_RGB;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

/**
 * Item / ability rarity palette — the WoW-classic canon (white → green →
 * blue → purple → orange). Previously redefined (and divergent) in
 * Tooltip / ItemKit / ShopView; this is the single source of truth. The
 * matching outer-glow shadow per tier lives in `RARITY_GLOW`.
 */
export const RARITY = {
  common: RAW.white,
  uncommon: '#1eff00',
  rare: '#0070dd',
  epic: '#a335ee',
  legendary: '#ff8000',
} as const;

export type Rarity = keyof typeof RARITY;

/** Soft outer glow per rarity tier (derived from RARITY hues). */
export const RARITY_GLOW: Record<Rarity, string> = {
  common: 'none',
  uncommon: '0 0 8px rgba(30, 255, 0, 0.3)',
  rare: '0 0 8px rgba(0, 112, 221, 0.35)',
  epic: '0 0 10px rgba(163, 53, 238, 0.4)',
  legendary: '0 0 12px rgba(255, 128, 0, 0.45)',
};

/**
 * Per-class identity colors, keyed by the lowercase CharacterClass value.
 * Previously redefined (and divergent — three different "warrior reds") in
 * PlayerList / PlayerInfoPanel / ClassSwitcherTopBar / QuickClassPickerScreen;
 * this is the single source of truth. Use `classColor(name)` for a
 * fallback-safe lookup.
 */
export const CLASS_COLORS: Record<string, string> = {
  warrior: '#c0392b',
  mage: '#3498db',
  hunter: '#27ae60',
  paladin: '#f1c40f',
  rogue: '#8e44ad',
  shaman: '#00B3A4',
  priest: '#bdc3c7',
  warlock: '#7D3C98',
  druid: '#6B8E23',
  dev: '#e67e22',
};

/** Fallback-safe class-color lookup (unknown class → neutral grey). */
export function classColor(className?: string): string {
  const canonical = resolveToCharacterClass(className);
  if (canonical && CLASS_COLORS[canonical]) return CLASS_COLORS[canonical];
  return CLASS_COLORS[(className ?? '').toLowerCase()] ?? '#95a5a6';
}

/**
 * Per-class identity emoji, keyed by the lowercase CharacterClass value.
 * Previously redefined (and divergent) in PlayerList / PlayerInfoPanel /
 * ClassSwitcherTopBar / QuickClassPickerScreen / CharacterCustomizer /
 * ActionDispatcher; this is the single source of truth. The rogue glyph
 * was split (🗡️ vs ✨) and ActionDispatcher's switch was missing the case
 * entirely — routing through this map fixes both. Use `classIcon(name)`
 * for a fallback-safe lookup.
 */
export const CLASS_ICONS: Record<string, string> = {
  warrior: '⚔️',
  mage: '🔮',
  hunter: '🏹',
  paladin: '🛡️',
  rogue: '🗡️',
  shaman: '🌩️',
  priest: '✨',
  warlock: '😈',
  druid: '🌿',
  dev: '🧪',
};

/** Fallback-safe class-icon lookup (unknown class → neutral glyph). */
export function classIcon(className?: string): string {
  const canonical = resolveToCharacterClass(className);
  if (canonical && CLASS_ICONS[canonical]) return CLASS_ICONS[canonical];
  return CLASS_ICONS[(className ?? '').toLowerCase()] ?? '👤';
}

/**
 * Per-stat identity colors, keyed by the lowercase StatType enum value
 * (strength/attack/hp/…). Previously each Character/Stats panel
 * (Primary/Combat/Resource/Utility) inlined its own `getStatColor` map;
 * this is the single source of truth. The per-panel hues are preserved
 * 1:1 (no stat is colored by more than one panel, so the union has no
 * divergence to resolve). Use `statColor(stat)` for a fallback-safe lookup.
 */
export const STAT_COLORS: Record<string, string> = {
  // primary
  strength: '#ff6b6b',
  dexterity: '#54a0ff',
  intelligence: '#5f27cd',
  luck: '#00d2d3',
  // resources
  hp: '#ff4757',
  mp: '#3742fa',
  // combat
  attack: '#ff9f43',
  magicAttack: '#9b59b6',
  defense: '#1dd1a1',
  magicDefense: '#341f97',
  // utility
  accuracy: '#00d2d3',
  avoidability: '#ff9ff3',
  speed: '#feca57',
  jump: '#ff9ff3',
};

/** Fallback-safe stat-color lookup (unknown stat → brand orange). */
export function statColor(stat?: string): string {
  return STAT_COLORS[stat ?? ''] ?? RAW.orange;
}
